import type {
  MessageAttachmentView,
  MessageThreadListResponse,
  MessageThreadResponse,
  MessageThreadView,
  MessageView,
} from "../../../shared/api-types";

import { isWellFormedRdSelection } from "../../../shared/selected-rd";

// The RD's Messages (NO-925): which page state to render, what the list and a
// thread show, and how a message time reads. Pure and relative-import only,
// so `tsx --test` runs it.

export interface MessagesSearch {
  rdId?: string;
}

/** `?rdId=` is an admin's picked RD; anything else the server would refuse is dropped. */
export function validateMessagesSearch(
  search: Record<string, unknown>
): MessagesSearch {
  return isWellFormedRdSelection(search.rdId) ? { rdId: search.rdId } : {};
}

/** The list's key holds the admin's RD selection only: no Client name, no text. */
export function messagesQueryKey(rdId: string | undefined) {
  return ["messages", "threads", rdId ?? null] as const;
}

export function messageThreadQueryKey(patientId: string, rdId: string | undefined) {
  return ["messages", "thread", patientId, rdId ?? null] as const;
}

/**
 * How often the Messages list and an open thread read again (NO-927), so a
 * Client's reply shows without a reload. TanStack pauses a polling query while
 * the tab is hidden (`refetchIntervalInBackground` stays false). Each read
 * that returns content records its Clinical Read Event, like any other read
 * (ADR-0056 over-records rather than undercounts), so the interval is the
 * cost knob: ten seconds is a few rows a minute for a tab someone is looking
 * at.
 */
export const MESSAGES_POLL_INTERVAL_MS = 10_000;

export interface PollReadState {
  isError: boolean;
  /** True once a read has answered, so there is something on screen to keep. */
  hasData: boolean;
  /** The failed read's HTTP status, or null for a network failure. */
  errorStatus: number | null;
}

export type PollFailureView = "error_state" | "stale_hint" | "none";

/**
 * What a failed read shows. A background poll that fails (a network blip, a 502
 * during a deploy) keeps the conversation on screen with a small hint, since
 * TanStack keeps the last data. The full error state is for a first read that
 * failed, and for an answer that says the RD may no longer see it (401, 403,
 * 404), so a revoked thread does not stay on screen.
 */
export function pollFailureView(state: PollReadState): PollFailureView {
  if (!state.isError) return "none";

  const refused =
    state.errorStatus === 401 || state.errorStatus === 403 || state.errorStatus === 404;

  return state.hasData && !refused ? "stale_hint" : "error_state";
}

/**
 * Poll while the last read answered, and past a transient failure while there
 * is data to keep fresh. Stop when the full error state shows, so a first
 * failure or a refusal is not hammered.
 */
export function messagesRefetchInterval(state: PollReadState): number | false {
  const view = pollFailureView(state);

  if (view === "error_state") return false;

  return view === "stale_hint"
    ? MESSAGES_FAILED_POLL_INTERVAL_MS
    : MESSAGES_POLL_INTERVAL_MS;
}

/** After a failed poll the next try waits longer, so an outage costs a third of the requests. */
export const MESSAGES_FAILED_POLL_INTERVAL_MS = 30_000;

export type MessagesPageState = "blank" | "not_found" | "page";

/**
 * The single-rail frame only, and only with `patient-messaging` on for a
 * viewer who reads Appointments (their own, or any RD's through the picker):
 * everyone else gets the router's 404, like any other flagged page. Blank
 * while the flags and the viewer load.
 */
export function messagesPageState(input: {
  frame: "rail" | "classic" | null;
  featuresLoading: boolean;
  messagingEnabled: boolean;
  canReadAppointments: boolean;
}): MessagesPageState {
  if (input.frame !== "rail") return "not_found";

  if (input.featuresLoading) return "blank";

  return input.messagingEnabled && input.canReadAppointments ? "page" : "not_found";
}

/**
 * The patient page's Messages link needs both flags and the same reach the
 * Messages page itself requires.
 */
export function showsPatientMessagesLink(input: {
  patientDetailEnabled: boolean;
  messagingEnabled: boolean;
  canReadOwnAppointments: boolean;
}): boolean {
  return (
    input.patientDetailEnabled && input.messagingEnabled && input.canReadOwnAppointments
  );
}

export type MessageListView =
  | {
      kind: "list";
      threads: Extract<MessageThreadListResponse, { status: "ok" }>["threads"];
    }
  | { kind: "empty" }
  | { kind: "no_selection" }
  | { kind: "unavailable" };

export function messageListView(response: MessageThreadListResponse): MessageListView {
  if (response.status === "no_selection") return { kind: "no_selection" };

  if (response.status === "unavailable") return { kind: "unavailable" };

  return response.threads.length === 0
    ? { kind: "empty" }
    : { kind: "list", threads: response.threads };
}

export type ThreadPageView =
  | {
      kind: "ready";
      client: Extract<MessageThreadResponse, { status: "ok" }>["client"];
      threads: MessageThreadView[];
    }
  | { kind: "no_selection" }
  | { kind: "unavailable" };

export function threadPageView(response: MessageThreadResponse): ThreadPageView {
  if (response.status === "no_selection") return { kind: "no_selection" };

  if (response.status === "unavailable") return { kind: "unavailable" };

  return { kind: "ready", client: response.client, threads: response.threads };
}

/**
 * The sender line: the name on file, else the role. A staff sender who cannot
 * be resolved is "NDD staff"; the thread's RD is never guessed.
 */
export function senderLabel(message: MessageView): string {
  const name = message.senderName?.trim();
  if (name) return name;

  return message.authorKind === "client" ? "Client" : "NDD staff";
}

/** "Sep 30, 2:15 PM", with the year added when it is not the current one. */
export function messageTimeLabel(iso: string, timezone: string, now: Date): string {
  const date = new Date(iso);
  const yearOf = (value: Date) =>
    new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(
      value
    );
  const sameYear = yearOf(date) === yearOf(now);

  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

const ATTACHMENT_KIND_LABELS: Record<MessageAttachmentView["kind"], string> = {
  document: "Document",
  image: "Image",
  audio: "Audio",
};

/** A non-downloading placeholder (downloads are NO-930): the kind and the file name. */
export function attachmentLabel(attachment: MessageAttachmentView): string {
  return `${ATTACHMENT_KIND_LABELS[attachment.kind]}: ${attachment.fileName}`;
}

/**
 * Older messages loaded into a thread, oldest page first. `cursor` is the next
 * `before` to send, null once the thread's start is reached.
 */
export interface OlderMessages {
  messages: MessageView[];
  cursor: string | null;
  status: "idle" | "loading" | "failed";
}

/** The older page prepended to what was already loaded above the first page. */
export function withOlderPage(
  previous: OlderMessages | undefined,
  page: MessageThreadView
): OlderMessages {
  return {
    messages: [...page.messages, ...(previous?.messages ?? [])],
    cursor: page.olderCursor,
    status: "idle",
  };
}

/** What the thread shows above its first page: loaded older messages, and the next cursor. */
export function olderView(
  thread: MessageThreadView,
  older: OlderMessages | undefined
): { messages: MessageView[]; cursor: string | null; status: OlderMessages["status"] } {
  return {
    messages: [...(older?.messages ?? []), ...thread.messages],
    cursor: older ? older.cursor : thread.olderCursor,
    status: older?.status ?? "idle",
  };
}

/**
 * Identifies which first pages the loaded older pages sit above. When a refetch
 * moves a thread's first page (new messages arrive), older pages loaded for
 * the previous first page would leave a gap, so they are dropped.
 */
export function threadsPageKey(threads: MessageThreadView[]): string {
  return threads
    .map((thread) => `${thread.threadId}:${thread.olderCursor ?? ""}`)
    .join(",");
}

export const DELETED_MESSAGE_LABEL = "Message deleted";
